
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { StoreModel } from '@/models/store';
import ActionButton from '@/components/ActionButton.vue';
import ForecastSetting from '@/models/forecast_setting';
import { EventBus } from "@/main";
import { FEATURES } from '@/config/featureConfig';
import { DimensionModel } from '@/models/dimension';
import { FilterDimensionList } from '@/collections/filter_dimensions';

interface ExclusionRule {
  field: string;
  condition: string;
  value: string[];
}

interface ForecastSettings {
  options: {
    methodology: string;
    recalculation_frequency: string;
    timeframe: {
      forecast_by: string;
      number_of_periods: number;
    };
    weighted_moving_average: {
      weights: number[];
    };
    yearly_trend: {
      enabled: boolean;
      comparison_base: string;
    };
    seasonal_adjustment: {
      enabled: boolean;
      comparison_base: string;
    };
    combined_method: {
      weights: {
        weighted_moving_average: number;
        yearly_trend: number;
        seasonal_adjustment: number;
      };
    };
    service_level: string;
    exclusion_rules: ExclusionRule[];
  };
}

@Component({
  components: {
    ActionButton,
  }
})
export default class InventoryForecastSettings extends Vue {
  @Prop({ default: () => new StoreModel() })
  public store!: StoreModel;

  @Prop({ default: () => [] })
  private timeRangeLimit!: string[];

  @Prop({ default: false })
  private isAthenaFinished!: boolean;

  private saving: boolean = false;
  private loading: boolean = false;
  private weightDialog: boolean = false;
  private editedWeight: number = 0;
  private editedWeightIndex: number = -1;
  private forecastSetting: ForecastSetting = new ForecastSetting();
  private recalculateLoading: boolean = false;
  private saveDialog: boolean = false;
  private initialMethodology: string | null = null;
  private exclusionPanel: number | null = null;
  private exclusionRuleState: {
    loading: boolean;
    items: string[];
    search: string | null;
  }[] = [];
  private searchTimeouts: Map<number, any> = new Map();
  private searchDebounceDelay: number = 400;

  private defaultSettings: ForecastSettings = {
    options: {
      methodology: 'simple_average',
      recalculation_frequency: 'monthly',
      timeframe: {
        forecast_by: 'month',
        number_of_periods: 6
      },
      weighted_moving_average: {
        weights: [6, 5, 4, 3, 2, 1]
      },
      yearly_trend: {
        enabled: true,
        comparison_base: 'last_year'
      },
      seasonal_adjustment: {
        enabled: true,
        comparison_base: 'last_year'
      },
      combined_method: {
        weights: {
          weighted_moving_average: 0.5,
          yearly_trend: 0.25,
          seasonal_adjustment: 0.25
        }
      },
      service_level: 'high',
      exclusion_rules: [],
    }
  };

  private settings: ForecastSettings = { ...this.defaultSettings };

  private exclusionColumns = [
    { text: 'Order Tags', value: 'order_tags' },
  ];

  private exclusionConditions = [
    { text: 'Equal', value: 'eq' },
    { text: 'Not equal', value: 'ne' },
    { text: 'Contains', value: 'contains' },
    { text: 'Does not contain', value: 'notContains' },
    { text: 'Is blank', value: 'null' },
    { text: 'Is not blank', value: 'notNull' },
    { text: 'Is lower than', value: 'lt' },
    { text: 'Is lower than or equal to', value: 'lte' },
    { text: 'Is greater than', value: 'gt' },
    { text: 'Is greater than or equal to', value: 'gte' },
  ];

  private forecastByOptions = [
    { text: 'Month', value: 'month' },
    // { text: 'Week', value: 'week' },
    // { text: 'Day', value: 'day' }
  ];

  private comparisonBaseOptions = [
    { text: 'Last Year', value: 'last_year' },
  ];

  private serviceLevelOptions = [
    { text: 'High - 90% chance sales are within the forecast', value: 'high' },
    { text: 'Medium - 50% chance sales are within the forecast', value: 'medium' },
    { text: 'Low - 10% chance sales are within the forecast', value: 'low' }
  ];

  get methodologyOptions() {
    const baseOptions = [
      { 
        text: 'Simple average moving',
        value: 'simple_average' 
      },
      { 
        text: 'Computing model', 
        value: 'combined_weighted' 
      }
    ];

    if (this.store.isValidateRule(FEATURES.AI_ML_DEMAND_FORECASTING, 'unlocked')) {
      baseOptions.push({
        text: 'Smart model (using AI)',
        value: 'ai'
      });
    }

    return baseOptions;
  }

  private recalculationOptions = [
    { text: 'Daily', value: 'daily' },
    { text: 'Weekly', value: 'weekly' },
    { text: 'Monthly', value: 'monthly' }
  ];

  get methodology() {
    return this.settings.options.methodology;
  }

  get showRecalculationFrequency() {
    return ['combined_weighted', 'ai'].includes(this.settings.options.methodology);
  }

  get methodologyDescription(): string {
    const descriptions: { [key: string]: string } = {
      simple_average: 'Uses a straightforward average of historical data to predict future values. Best for stable, consistent data patterns.',
      combined_weighted: 'Advanced model that combines moving averages with yearly trends. Ideal for data with seasonal patterns and long-term trends.',
      ai: 'Advanced AI-powered model that learns from historical patterns to provide highly accurate forecasts. Ideal for complex, dynamic data patterns.'
    };
    return descriptions[this.settings.options.methodology] || '';
  }

  get serviceLevelDescription(): string {
    const descriptions: { [key: string]: string } = {
      high: 'Help to maintain higher safety stock levels to minimize stockouts. Best for essential products or when customer satisfaction is the priority.',
      medium: 'Provides a balanced approach to inventory management.',
      low: 'Help to minimize inventory levels to optimize cash flow and reduce holding costs. Suitable for non-essential or slow-moving items.'
    };
    return this.settings.options?.service_level 
      ? descriptions[this.settings.options.service_level] 
      : '';
  }

  @Watch('settings.options.timeframe.number_of_periods')
  private onNumberOfPeriodsChange(newValue: number) {
    // Ensure weighted_moving_average and weights exist
    if (!this.settings.options.weighted_moving_average) {
      this.settings.options.weighted_moving_average = { weights: [] };
    }

    // Generate new weights array based on number of periods
    const currentWeights = this.settings.options.weighted_moving_average.weights || [];
    const newWeights = Array(newValue).fill(0).map((_, index) => {
      // If we have an existing weight, use it, otherwise generate new weight
      if (index < currentWeights.length) {
        return currentWeights[index];
      }
      // New weights decrease linearly from the highest period to the lowest
      return newValue - index;
    });
    
    // Update weights array
    this.settings.options.weighted_moving_average.weights = newWeights;
  }

  @Watch('settings.options.methodology')
  private onMethodologyChange(newValue: string) {
    if (newValue === 'simple_average') {
      this.settings.options.recalculation_frequency = 'daily';
    } else if (newValue === 'combined_weighted') {
      this.settings.options.recalculation_frequency = 'daily';
    } else if (newValue === 'ai') {
      this.settings.options.recalculation_frequency = 'monthly';
    }
  }

  private editWeight(index: number) {
    this.editedWeightIndex = index;
    this.editedWeight = this.settings.options.weighted_moving_average.weights[index];
    this.weightDialog = true;
  }

  private saveWeight() {
    if (this.editedWeightIndex >= 0) {
      this.settings.options.weighted_moving_average.weights[this.editedWeightIndex] = this.editedWeight;
    }
    this.weightDialog = false;
  }

  private autoDistributeWeights() {
    const numPeriods = this.settings.options.timeframe.number_of_periods;
    this.settings.options.weighted_moving_average.weights = Array(numPeriods)
      .fill(0)
      .map((_, index) => numPeriods - index);
  }

  private recommendOptimalWeights() {
    let weights = {
      weighted_moving_average: 0,
      yearly_trend: 0,
      seasonal_adjustment: 0
    };

    const defaultWeights = this.defaultSettings.options.combined_method.weights;

    // Count enabled components
    let enabledCount = 1; // Always include weighted_moving_average
    if (this.settings.options.yearly_trend.enabled) enabledCount++;
    if (this.settings.options.seasonal_adjustment.enabled) enabledCount++;

    // Distribute weights evenly among enabled components
    const baseWeight = 1 / enabledCount;
    
    // Always include weighted_moving_average
    weights.weighted_moving_average = defaultWeights.weighted_moving_average;
    
    // Add weights for enabled components
    if (this.settings.options.yearly_trend.enabled) {
      weights.yearly_trend = defaultWeights.yearly_trend;
    }
    
    if (this.settings.options.seasonal_adjustment.enabled) {
      weights.seasonal_adjustment = defaultWeights.seasonal_adjustment;
    }
    // normalize weights and rounding to 2 decimal places
    const total = Object.values(weights).reduce((sum, weight) => sum + weight, 0);
    for (const key in weights) {
      weights[key as keyof typeof weights] = Math.round(weights[key as keyof typeof weights] / total * 100) / 100;
    }
    // ensure the sum of weights is 1, auto distribute to the weighted_moving_average 
    weights.weighted_moving_average = 1 - Object.entries(weights).reduce((sum, [k, weight]) => 
      k !== 'weighted_moving_average' ? sum + weight : sum, 0);
    // round weighted_moving_average
    weights.weighted_moving_average = Math.round(weights.weighted_moving_average * 100) / 100;
    // Update the settings
    this.settings.options.combined_method.weights = weights;
  }

  private addExclusionRule() {
    if (!this.settings.options.exclusion_rules) {
      this.$set(this.settings.options, 'exclusion_rules', []);
    }
    this.settings.options.exclusion_rules.push({
      field: 'order_tags',
      condition: 'contains',
      value: []
    });
    this.exclusionRuleState.push({
      loading: false,
      items: [],
      search: null
    });
  }

  private removeExclusionRule(index: number) {
    this.settings.options.exclusion_rules.splice(index, 1);
    this.exclusionRuleState.splice(index, 1);
  }

  private debouncedFetchOrderTags(index: number) {
    if (this.searchTimeouts.has(index)) {
      clearTimeout(this.searchTimeouts.get(index));
    }

    const timeoutId = setTimeout(() => {
      // ensure the state exists before fetching
      if (this.exclusionRuleState[index]) {
        this.fetchOrderTags(index);
      }
    }, this.searchDebounceDelay);

    this.searchTimeouts.set(index, timeoutId);
  }

  private async fetchOrderTags(index: number) {
    const ruleState = this.exclusionRuleState[index];
    if (!ruleState || ruleState.loading) return;

    ruleState.loading = true;
    try {
      const dimension = new DimensionModel({ code: 'by_order_tag' });
      const dimensionValues = new FilterDimensionList();
      dimensionValues.dimension = dimension;
      
      await dimensionValues.fetch({ keyword: ruleState.search || '' });
      
      ruleState.items = dimensionValues.items.map(item => item.name);

    } catch (error) {
      console.error('Failed to fetch order tags:', error);
      ruleState.items = [];
    } finally {
      ruleState.loading = false;
    }
  }

  private async saveSettingsOnly() {
    this.saveDialog = false;
    this.saving = true;
    try {
      const forecastSetting = new ForecastSetting(this.settings.options);
      await forecastSetting.save();
      this.$emit('settings-saved', this.settings.options);
      EventBus.$emit('show-snackbar', {
        message: 'The forecast settings have been updated successfully.',
        color: "success",
      });
      if ((!this.initialMethodology || this.initialMethodology === 'simple_average')
        && this.settings.options.methodology !== 'simple_average') {
        window.location.reload();
      }
    } catch (error) {
      console.error('Failed to save forecast settings:', error);
      EventBus.$emit('show-snackbar', {
        message: 'Failed to save forecast settings',
        color: "error",
      });
    } finally {
      this.saving = false;
    }
  }

  private async saveAndRecalculate() {
    this.saveDialog = false;    
    this.saving = true;
    try {
      const forecastSetting = new ForecastSetting(this.settings.options);
      await forecastSetting.save();
      this.saving = false;
      EventBus.$emit('show-snackbar', {
        message: 'The forecast settings have been updated successfully.',
        color: "success",
      });
      this.$emit('settings-saved', this.settings.options);
      await new Promise(resolve => setTimeout(resolve, 2000));
      await this.handleRecalculate();
      if ((!this.initialMethodology || this.initialMethodology === 'simple_average')
        && this.settings.options.methodology !== 'simple_average') {
        window.location.reload();
      }
    } catch (error) {
      console.error('Failed to save forecast settings:', error);
      EventBus.$emit('show-snackbar', {
        message: 'Failed to save forecast settings',
        color: "error",
      });
    } finally {
      this.saving = false;
    }    
  }
  /**
   * Save the settings and recalculate the forecast if the methodology is not simple average
   */
  private async saveSettings() {
    // Show dialog instead of saving directly
    if (this.settings.options.methodology !== 'simple_average') {
      await this.saveAndRecalculate();
    } else {
      await this.saveSettingsOnly();
    }
  }

  private async handleRecalculate() {
    try {
      this.recalculateLoading = true;
      EventBus.$emit('show-snackbar', {
        message: 'The forecast calculation has been initiated. Please wait for the data to be updated in minute.',
        color: "info",
      });
      await this.forecastSetting.forceRecalculateSalesForecast();
      EventBus.$emit('show-snackbar', {
        message: 'The forecast calculation has been completed.',
        color: "success",
      });
    } catch (error) {
      console.error('Error recalculating forecast:', error);
      EventBus.$emit('show-snackbar', {
        message: 'Failed to calculate forecast: ' + error,
        color: "error",
        timeout: 10000
      });
    } finally {
      this.recalculateLoading = false;
    }
  }

  async created() {
    await this.loadForecastSetting();
  }

  private async loadForecastSetting() {
    this.loading = true;
    try {
      await this.forecastSetting.load();
      // Store initial methodology before merging settings
      this.initialMethodology = this.forecastSetting.options?.methodology || null;
      // Deep merge default settings with loaded settings   
      this.settings = {
        options: {
          ...this.defaultSettings.options,    
          ...this.forecastSetting.options,
          timeframe: {
            ...this.defaultSettings.options.timeframe,
            ...this.forecastSetting.options?.timeframe
          },
          weighted_moving_average: {
            weights: this.forecastSetting.options?.weighted_moving_average?.weights || 
                    this.defaultSettings.options.weighted_moving_average.weights
          },
          yearly_trend: {
            ...this.defaultSettings.options.yearly_trend,
            ...this.forecastSetting.options?.yearly_trend
          },
          seasonal_adjustment: {
            ...this.defaultSettings.options.seasonal_adjustment,
            ...this.forecastSetting.options?.seasonal_adjustment
          },
          combined_method: {
            weights: {
              ...this.defaultSettings.options.combined_method.weights,
              ...this.forecastSetting.options?.combined_method?.weights
            }
          },
          exclusion_rules: this.forecastSetting.options?.exclusion_rules || [],
        }
      };
      // Initialize state for existing rules
      this.exclusionRuleState = (this.settings.options.exclusion_rules || []).map(() => ({
        loading: false,
        items: [],
        search: null
      }));

      if (this.settings.options.exclusion_rules && this.settings.options.exclusion_rules.length > 0) {
        this.exclusionPanel = 0;
      }
    } catch (error) {
      console.error('Failed to load forecast settings:', error);
      this.settings = { ...this.defaultSettings };
      this.initialMethodology = null;
    } finally {
      this.loading = false;
    }
  }
}
